import React, { useState } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Flashbar, {
	FlashbarProps,
} from '@amzn/awsui-components-console/flashbar';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import ExportDialog from 'src/pages/ViewWorkflow/ExportDialog';
import { exportWorkflow } from 'src/services/workflowExport/exportService';
import { ExportOptions } from 'src/services/workflowExport/types';
import { useAuthContext } from 'src/providers/AuthContext';
import { useBindleAuthorization } from 'src/hooks/useBindleAuthorization';
import {
	getBindleLockForNodeType,
	getBindleLockAuthorizationRequest,
	SNOW_INTERNAL_BINDLE,
} from 'src/authentication/bindle-utils';
import { WORKFLOW_DETAILS_METADATA_MAP } from './ViewWorkflowConstants';
import ViewWorkflowDesignerCanvas from 'src/components/createWorklow/ViewWorkflowDesignerCanvas/ViewWorkflowDesignerCanvas';
import ViewWorkflowTasksTable from 'src/components/createWorklow/ViewWorkflowTasksTable/ViewWorkflowTasksTable';
import WorkflowAttributesTable from './WorkflowAttributesTable';

export default function ViewWorkflowContent() {
	const { taskDefinitions, dependencies } = useStore(
		(state: Store) => state.WFDefinition
	);

	const workflowCreationMetaData = useStore(
		(state: Store) => state.workflowCreationMetaData
	);

	const { userName } = useAuthContext();
	const lockId = getBindleLockForNodeType(SNOW_INTERNAL_BINDLE);
	const { isAuthorized: canExport } = useBindleAuthorization(
		userName,
		getBindleLockAuthorizationRequest([lockId])
	);

	const [exportDialogVisible, setExportDialogVisible] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [flashBarItems, setFlashBarItems] = useState<
		FlashbarProps.MessageDefinition[]
	>([]);

	const handleExportWorkflow = async (options: ExportOptions) => {
		setIsExporting(true);
		try {
			const completeWorkflow = {
				taskDefinitions,
				dependencies,
				workflowDefinition: workflowCreationMetaData,
			};
			const result = await exportWorkflow(completeWorkflow, options);

			if (result.success) {
				setFlashBarItems([
					{
						type: 'success',
						content: `Workflow exported successfully as ${result.filename}`,
						dismissible: true,
						onDismiss: () => setFlashBarItems([]),
						id: 'export-success',
					},
				]);
			} else {
				setFlashBarItems([
					{
						type: 'error',
						content: result.error || 'Failed to export workflow',
						dismissible: true,
						onDismiss: () => setFlashBarItems([]),
						id: 'export-error',
					},
				]);
			}
		} catch (error) {
			setFlashBarItems([
				{
					type: 'error',
					content:
						error instanceof Error
							? error.message
							: 'An unexpected error occurred',
					dismissible: true,
					onDismiss: () => setFlashBarItems([]),
					id: 'export-error',
				},
			]);
		} finally {
			setIsExporting(false);
			setExportDialogVisible(false);
		}
	};

	const workflowDetailsMetadataMapData = WORKFLOW_DETAILS_METADATA_MAP(
		workflowCreationMetaData
	);

	return (
		<SpaceBetween size="l">
			{flashBarItems.length > 0 && <Flashbar items={flashBarItems} />}
			<ExportDialog
				isOpen={exportDialogVisible}
				onClose={() => setExportDialogVisible(false)}
				onExport={handleExportWorkflow}
				workflowName={workflowCreationMetaData.workflowDefinitionName}
				isExporting={isExporting}
			/>
			<Container
				header={
					<Header
						variant="h2"
						actions={
							<SpaceBetween direction="horizontal" size="xs">
								<Button
									href={`#/simulateWorkflow?workflowId=${workflowCreationMetaData?.workflowId || ''}&workflowName=${encodeURIComponent(workflowCreationMetaData?.workflowDefinitionName || '')}`}
								>
									Simulate
								</Button>
								{canExport && (
									<Button
										iconName="download"
										onClick={() => setExportDialogVisible(true)}
									>
										Export
									</Button>
								)}
							</SpaceBetween>
						}
					>
						Workflow Details
					</Header>
				}
			>
				<ColumnLayout columns={4} variant="text-grid">
					{workflowDetailsMetadataMapData.map((item, index) => (
						<SpaceBetween key={index} size="xxs">
							<Box variant="awsui-key-label">{item.label}</Box>
							<Box>{item.value || '-'}</Box>
						</SpaceBetween>
					))}
				</ColumnLayout>
			</Container>
			<ViewWorkflowDesignerCanvas
				taskDefinitions={taskDefinitions}
				dependencies={dependencies}
			/>
			<ViewWorkflowTasksTable taskDefinitions={taskDefinitions} />
			<WorkflowAttributesTable
				taskDefinitions={taskDefinitions}
				workflowId={workflowCreationMetaData?.workflowId || ''}
			/>
		</SpaceBetween>
	);
}
